import {useState, useMemo} from "react";
import moment from "moment";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Avatar,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import UserInfo from "src/components/common/UserInfo";
import UserInfoDrawer from "src/components/common/UserInfoDrawer";
import {asyncRequest} from "src/services/network";
import {alert} from "src/services/alert";
import {IMG_BASE_URL} from "src/config/constants";

const TABLE_HEAD = [
  {id: "type", label: "类型", alignRight: false, width: 160},
  {id: "sourceInfo", label: "来源", alignRight: false, width: 150},
  {id: "userInfo", label: "用户", alignRight: false, width: 150},
  {id: "num", label: "数量", alignRight: false, width: 100},
  {id: "before_balance", label: "变动前余额", alignRight: false, width: 120},
  {id: "after_balance", label: "变动后余额", alignRight: false, width: 120},
  {id: "remark", label: "备注", alignRight: false},
  {id: "create_time", label: "时间", alignRight: false, width: 160},
];

const TYPE_OPTIONS = [
  {value: "", label: "全部"},
  {value: 1, label: "兑换订单扣除手续费"},
  {value: 2, label: "后台操作"},
  {value: 4, label: "下级购买手续费奖励"},
  {value: 10, label: "转出"},
  {value: 11, label: "转入"},
  {value: 39, label: "系统奖励"},
];

const TYPE_COLOR_MAP = {
  1: "warning",
  2: "info",
  4: "success",
  10: "error",
  11: "primary",
  39: "secondary",
};

export default function DragonBeadLog() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  // 默认时间：昨天18:30:00 ~ 今天18:30:00
  const defaultTimeRange = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(today.getTime() + 18 * 3600000);
    const start = new Date(end.getTime() - 86400000);
    const fmt = (d) => moment(d).format("YYYY-MM-DDTHH:mm");
    return {startDate: fmt(start), endDate: fmt(end)};
  }, []);

  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    platId: "",
    type: "",
    startDate: defaultTimeRange.startDate,
    endDate: defaultTimeRange.endDate,
  });

  async function getItems(filterOverride) {
    setItems(null);
    const queryFilter = filterOverride || filter;
    const {data, count} = await asyncRequest({
      path: "/jingxi/dragon-bead-log/list",
      data: queryFilter,
    });
    setItems(data || []);
    setTotal(count);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = {...filter, page: 0, size: newSize};
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = {...filter, page: newPage};
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({
      page: 0,
      size: 20,
      platId: "",
      type: "",
      startDate: defaultTimeRange.startDate,
      endDate: defaultTimeRange.endDate,
    });
  }

  async function handleExport() {
    const {msg} = await asyncRequest({
      path: "/jingxi/dragon-bead-log/export",
      data: filter,
    });
    alert(msg, "success");
  }

  function openUserDrawer(info) {
    if (info && info.mainUser) {
      setTargetUser({
        uid: info.mainUser.uid,
        avatar: info.mainUser.avatar,
        nickname: info.mainUser.nickname,
      });
    } else if (info && info.plat_id) {
      setTargetUser({
        uid: info.plat_id,
        avatar: info.avatar,
        nickname: info.user_nickname,
      });
    }
  }

  function renderUserCell(info) {
    if (!info) return <Typography variant="body2">-</Typography>;

    const mainUser = info.mainUser;
    if (mainUser) {
      return (
        <UserInfo
          avatar={mainUser.avatar}
          uid={mainUser.uid}
          nickname={mainUser.nickname}
          enablePreview
        />
      );
    }

    // 没有主库信息时，显示金银通用户信息
    const avatarSrc = info.avatar && !info.avatar.startsWith("http")
      ? `${IMG_BASE_URL}${info.avatar}`
      : info.avatar;

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{cursor: "pointer"}}
        onClick={() => openUserDrawer(info)}
      >
        <Avatar src={avatarSrc} sx={{width: 28, height: 28}}/>
        <Stack>
          <Typography variant="body2">{info.user_nickname || `ID:${info.id}`}</Typography>
          <Typography variant="caption" color="text.secondary">UID: {info.plat_id || "-"}</Typography>
        </Stack>
      </Stack>
    );
  }

  function renderSourceCell(sourceInfo, userInfo) {
    if (!sourceInfo) return <Typography variant="body2">-</Typography>;

    if (sourceInfo.type === "system") {
      return (
        <Chip size="small" label="系统" color="default" variant="outlined"/>
      );
    }

    if (sourceInfo.type === "self") {
      return (
        <Chip size="small" label="自己" color="primary" variant="outlined"/>
      );
    }

    if (sourceInfo.type === "other") {
      return renderUserCell(sourceInfo);
    }

    // unknown
    return <Typography variant="body2">ID:{sourceInfo.id}</Typography>;
  }

  const isNotFound = items && items.length === 0;

  return (
    <Page title="银豆日志">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            银豆日志查询
          </Typography>
        </Stack>

        <Card>
          <ListToolbar
            showSearch={false}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  style={{width: 220}}
                  label="开始时间"
                  InputLabelProps={{shrink: true}}
                  value={filter.startDate}
                  onChange={(e) => setFilter({...filter, startDate: e.target.value})}
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  style={{width: 220}}
                  label="结束时间"
                  InputLabelProps={{shrink: true}}
                  value={filter.endDate}
                  onChange={(e) => setFilter({...filter, endDate: e.target.value})}
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  style={{width: 180}}
                  label="鲸世优选UID"
                  placeholder="鲸世优选UID"
                  autoComplete="off"
                  value={filter.platId}
                  onChange={(e) => setFilter({...filter, platId: e.target.value})}
                />
              </Grid>
              <Grid item>
                <FormControl size="small" style={{minWidth: 180}}>
                  <InputLabel>类型</InputLabel>
                  <Select
                    label="类型"
                    value={filter.type}
                    onChange={(e) => setFilter({...filter, type: e.target.value})}
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={() => getItems()}>
                    查询
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleReset}>
                    重置
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleExport} disabled={!hasQueried}>
                    导出
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer sx={{minWidth: 1100}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                {!hasQueried ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="text.secondary">请设置查询条件后点击查询</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <CircularProgress/>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => {
                      const {id, type, typeName, userInfo, sourceInfo, num, before_balance, after_balance, remark, create_time} = row;
                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCell align="left">
                            <Chip
                              size="small"
                              label={typeName}
                              color={TYPE_COLOR_MAP[type] || "default"}
                            />
                          </TableCell>
                          <TableCell align="left">
                            {renderSourceCell(sourceInfo, userInfo)}
                          </TableCell>
                          <TableCell align="left">
                            {renderUserCell(userInfo)}
                          </TableCell>
                          <TableCell align="left">
                            <Typography
                              variant="body2"
                              color={num > 0 ? "success.main" : "error.main"}
                            >
                              {num > 0 ? `+${num}` : num}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">{before_balance}</TableCell>
                          <TableCell align="left">{after_balance}</TableCell>
                          <TableCell align="left">{remark || "-"}</TableCell>
                          <TableCell align="left">
                            {create_time ? moment(create_time).format("YYYY-MM-DD HH:mm:ss") : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{py: 5}}>
                        <SearchNotFound/>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination
            total={total}
            page={filter.page}
            size={filter.size}
            onChange={handlePageChange}
          />
        </Card>
      </Container>

      {targetUser && (
        <UserInfoDrawer
          open
          uid={targetUser.uid}
          avatar={targetUser.avatar}
          nickname={targetUser.nickname}
          onClose={() => setTargetUser(null)}
        />
      )}
    </Page>
  );
}
